/**
 * Non-interface helper methods for LoaderNormal
 * Contains private helpers and methods not part of ILoader interface
 *
 * These methods are attached to LoaderNormal.prototype in the main file
 */

import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { JsonUtility } from '../../app/jsonUtility.js';
import { JsonArray, JsonObject } from '../../app/json/jsonValue.js';
import { ResourceStatistics, TemplateEntry } from '../../model/index.js';
import { DynamicConfigMap } from '../../registry/dynamicConfigMap.js';
import { CommonUtil } from '../../common/commonUtil.js';
import type { IConfig } from '../../config/iConfig.js';
import type { LoaderNormal } from './loaderNormal.js';

/**
 * Accessor methods to be attached to LoaderNormal prototype
 */
export const accessorMethods = {
    // #region Template Existence (Non-Interface)

    /**
     * Checks if a template exists by using GetTemplate and comparing with DefaultReplaceHtml
     * This ensures consistency with GetTemplate's fallback logic (AppView, SearchAppSites)
     */
    hasTemplate(this: LoaderNormal, appSite: string, currentTemplateName: string, appFileOrPlaceholderOrSlotholderName: string, appView: string | null = null): boolean {
        // Use GetTemplate which already handles AppView and SearchAppSites fallback
        const { html } = this.getTemplate(appSite, currentTemplateName, appFileOrPlaceholderOrSlotholderName, appView);

        // Template exists if it's not the default template (DefaultReplaceHtml)
        return html !== null && html !== this._config!.defaultReplaceHtml;
    },

    // #endregion

    // #region Configuration (Non-Interface)

    /**
     * Skip patterns for Dynamic View handlers (comma-delimited or "*").
     * Mirrors C# `LoaderNormal_Accessors.cs : public string? SkipDynamicView`.
     * Attached to LoaderNormal.prototype via Object.defineProperties; `this` is bound to the instance at call time.
     */
    get skipDynamicView(): string | null { return (this as unknown as LoaderNormal)._skipDynamicView; },
    set skipDynamicView(value: string | null) { (this as unknown as LoaderNormal)._skipDynamicView = value; },

    /**
     * Skip patterns for Dynamic Data handlers (comma-delimited or "*").
     * Mirrors C# `LoaderNormal_Accessors.cs : public string? SkipDynamicData`.
     */
    get skipDynamicData(): string | null { return (this as unknown as LoaderNormal)._skipDynamicData; },
    set skipDynamicData(value: string | null) { (this as unknown as LoaderNormal)._skipDynamicData = value; },

    /**
     * Skip patterns for Dynamic Action handlers (comma-delimited or "*").
     * Mirrors C# `LoaderNormal_Accessors.cs : public string? SkipDynamicAction`.
     */
    get skipDynamicAction(): string | null { return (this as unknown as LoaderNormal)._skipDynamicAction; },
    set skipDynamicAction(value: string | null) { (this as unknown as LoaderNormal)._skipDynamicAction = value; },

    /**
     * Gets SearchAppSites for the specified AppSite
     * In server mode: delegates to IConfig.getSearchAppSitesForAppSite
     * In client mode: returns stored SearchAppSites from API response
     */
    getSearchAppSitesForAppSite(this: LoaderNormal, appSite: string): string {
        return this._config!.getSearchAppSitesForAppSite(appSite);
    },

    // #endregion

    // #region Statistics (Non-Interface)

    /**
     * Gets resource statistics from loader's cached data
     * PERFORMANCE: Lazy computation with caching - zero impact on normal operations
     */
    getResourceStatistics(this: LoaderNormal, appSite: string): ResourceStatistics {
        // Return cached result if available (O(1) lookup)
        if (this._resourceStatsCache.has(appSite)) {
            return this._resourceStatsCache.get(appSite)!;
        }

        // Compute statistics lazily (only when explicitly requested)
        const stats = this._computeResourceStatistics(appSite);

        // Cache for subsequent calls
        this._resourceStatsCache.set(appSite, stats);

        return stats;
    },

    // #endregion

    // #region Statistics Helpers (Private)

    /**
     * Computes resource statistics from loader's cached data
     * Called only once per appSite, result is cached
     * @private
     */
    _computeResourceStatistics(this: LoaderNormal, appSite: string): ResourceStatistics {
        const stats = new ResourceStatistics();

        // Get template/JSON stats from _appSiteCache
        const cached = this._appSiteCache.get(appSite);
        if (cached && cached.templates) {
            for (const [, value] of cached.templates.entries()) {
                // T - Count templates with actual HTML content
                if (value.html && value.html !== this._config!.defaultReplaceHtml) {
                    stats.templateCount++;
                }

                // J, K, A - Count JSON files, keys, and arrays
                // LoaderNormal stores parsed JsonObject directly
                if (value.json) {
                    stats.jsonFileCount++;
                    (this.constructor as typeof LoaderNormal)._countJsonKeysAndArrays(value.json, stats);
                }
            }
        }

        // D - Count DynamicData handlers from cached config
        const dynamicDataConfig = this.getDynamicDataConfig(appSite);
        if (dynamicDataConfig) {
            stats.dynamicDataHandlerCount = (this.constructor as typeof LoaderNormal)._countHandlersInConfig(dynamicDataConfig);
        }

        // V - Count DynamicView handlers from cached config
        const dynamicViewConfig = this.getDynamicViewConfig(appSite);
        if (dynamicViewConfig) {
            stats.dynamicViewHandlerCount = (this.constructor as typeof LoaderNormal)._countHandlersInConfig(dynamicViewConfig);
        }

        return stats;
    },

    // #endregion

    // #region Loader State Diagnostic

    /**
     * Returns a JSON string representing the loader's internal cache state for diff/verification.
     * Used by round-trip testing to verify ConfigFile vs ConfigClient produce identical loader state.
     */
    getLoaderStateJson(this: LoaderNormal, appSite: string): string {
        const sb: string[] = [];
        sb.push('{');

        // Template keys
        sb.push('"TemplateKeys":[');
        const cached = this._appSiteCache.get(appSite);
        if (cached && cached.templates) {
            const sortedKeys = Array.from(cached.templates.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
            for (let i = 0; i < sortedKeys.length; i++) {
                if (i > 0) sb.push(',');
                sb.push('"'); sb.push(sortedKeys[i]); sb.push('"');
            }
        }
        sb.push('],');

        // GlobalData store
        sb.push('"GlobalDataStore":{');
        const gdKeys = Array.from(this._globalDataStore.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < gdKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(gdKeys[i]); sb.push('":');
            const gd = this._globalDataStore.get(gdKeys[i]);
            sb.push(gd.isObject ? JsonUtility.serializeObject(gd.getObject()) : 'null');
        }
        sb.push('},');

        // GlobalViewData store
        sb.push('"GlobalViewDataStore":{');
        const gvdKeys = Array.from(this._globalViewDataStore.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < gvdKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(gvdKeys[i]); sb.push('":');
            const gvd = this._globalViewDataStore.get(gvdKeys[i]);
            sb.push(gvd.isObject ? JsonUtility.serializeObject(gvd.getObject()) : 'null');
        }
        sb.push('},');

        // GlobalViewDataComponent store
        sb.push('"GlobalViewDataComponentStore":{');
        const gvdcKeys = Array.from(this._globalViewDataComponentStore.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < gvdcKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(gvdcKeys[i]); sb.push('":');
            const gvdc = this._globalViewDataComponentStore.get(gvdcKeys[i]);
            sb.push(gvdc.isObject ? JsonUtility.serializeObject(gvdc.getObject()) : 'null');
        }
        sb.push('},');

        // DynamicData config store (keys + handler count)
        sb.push('"DynamicDataConfigStore":{');
        const ddKeys = Object.keys(this._dynamicDataConfigStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < ddKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(ddKeys[i]); sb.push('":');
            const dd = this._dynamicDataConfigStore[ddKeys[i]];
            sb.push(dd ? dd.size.toString() : 'null');
        }
        sb.push('},');

        // DynamicView config store
        sb.push('"DynamicViewConfigStore":{');
        const dvKeys = Object.keys(this._dynamicViewConfigStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < dvKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(dvKeys[i]); sb.push('":');
            const dv = this._dynamicViewConfigStore[dvKeys[i]];
            sb.push(dv ? dv.size.toString() : 'null');
        }
        sb.push('},');

        // DynamicAction config store
        sb.push('"DynamicActionConfigStore":{');
        const daKeys = Object.keys(this._dynamicActionConfigStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < daKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(daKeys[i]); sb.push('":');
            const da = this._dynamicActionConfigStore[daKeys[i]];
            sb.push(da ? da.size.toString() : 'null');
        }
        sb.push('},');

        // DynamicData params store (keys only)
        sb.push('"DynamicDataParamsKeys":[');
        const ddpKeys = Array.from(this._dynamicDataParamsStore.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < ddpKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(ddpKeys[i]); sb.push('"');
        }
        sb.push('],');

        // DynamicView params store (keys only)
        sb.push('"DynamicViewParamsKeys":[');
        const dvpKeys = Array.from(this._dynamicViewParamsStore.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < dvpKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(dvpKeys[i]); sb.push('"');
        }
        sb.push('],');

        // DynamicAction params store (keys only)
        sb.push('"DynamicActionParamsKeys":[');
        const dapKeys = Array.from(this._dynamicActionParamsStore.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < dapKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(dapKeys[i]); sb.push('"');
        }
        sb.push(']');

        sb.push('}');
        return sb.join('');
    },

    getLoaderPerSiteStoresJson(this: LoaderNormal, appSite: string): string {
        const sb: string[] = [];
        this._serializePerSiteStores(sb, appSite);
        const json = sb.join('');
        const prefix = ',"PerSiteStores":';
        return json.startsWith(prefix) ? json.substring(prefix.length) : '{}';
    }

    // #endregion
};

/**
 * Static methods to be attached to LoaderNormal class
 */
export const accessorStaticMethods = {
    // #region Loading Templates (Private)

    /**
     * Loads templates from a single AppSite using IConfig content retrieval
     * Parses JSON to JsonObject during load for better performance (parse once, use many times)
     * @private
     */
    loadTemplatesFromSingleAppSite(config: IConfig, appSite: string): Map<string, TemplateEntry> {
        const result = new Map<string, TemplateEntry>();

        // Get all content names for this AppSite from IConfig
        const contentNames = config.getAllContentNames(appSite);
        if (contentNames.length === 0) {
            Logger.warn(`No content names found for AppSite: ${appSite}`, 'LoaderNormal');
            return result;
        }

        Logger.debug(`Loading templates for AppSite: ${appSite} (${contentNames.length} files)`, 'LoaderNormal');

        // Process each file
        for (const contentName of contentNames) {
            // Skip dynamic/global config entries — they are loaded separately into dedicated stores
            if (CommonUtil.isDynamicOrGlobalConfigName(contentName)) continue;

            const key = `${appSite.toLowerCase()}_${contentName.toLowerCase()}`;

            // Load HTML if available
            const htmlContent = config.getHtmlContent(appSite, contentName) || '';
            if (htmlContent) {
                Logger.debug(`Loaded HTML for ${key} (size: ${htmlContent.length})`, 'LoaderNormal');
            }

            // Load and parse JSON if available
            const jsonObj = config.getDataJson(appSite, contentName);
            if (jsonObj) {
                Logger.debug(`Loaded JSON for ${key} (${jsonObj.size} keys)`, 'LoaderNormal');
            }

            // Only add to result if we have HTML (or JSON-only for component reuse patterns)
            if (htmlContent || jsonObj) {
                result.set(key, new TemplateEntry(htmlContent, jsonObj));
                Logger.debug(`Added template: ${key} (html: ${htmlContent.length} bytes, json: ${jsonObj ? jsonObj.size : 0} keys)`, 'LoaderNormal');
            }
        }

        Logger.debug(`Loaded ${result.size} templates for ${appSite}`, 'LoaderNormal');
        return result;
    },

    BuildAppSitesToLoad(appSite: string, searchAppSites: string): string[] {
        const appSitesToLoad: string[] = [];
        const seen = new Set<string>();

        const addAppSite = (site: string | null | undefined): void => {
            if (!site) {
                return;
            }

            const trimmedSite = site.trim();
            if (!trimmedSite) {
                return;
            }

            const lowerSite = trimmedSite.toLowerCase();
            if (seen.has(lowerSite)) {
                return;
            }

            seen.add(lowerSite);
            appSitesToLoad.push(trimmedSite);
        };

        addAppSite(appSite);
        for (const searchAppSite of searchAppSites.split(',')) {
            addAppSite(searchAppSite);
        }

        return appSitesToLoad;
    },

    // #endregion

    // #region Skip Handler Helper

    /**
     * Checks if a handler should be skipped based on skip patterns
     */
    _shouldSkipHandler(handlerType: string, skipPatterns: string | null): boolean {
        if (!handlerType) {
            return true;
        }

        if (!skipPatterns || skipPatterns.trim() === '') {
            return false;
        }

        const normalizedPatterns = skipPatterns.trim();
        if (normalizedPatterns === '*') {
            return true;
        }

        const lowerHandlerType = handlerType.toLowerCase();
        for (const pattern of normalizedPatterns.split(',')) {
            const trimmedPattern = pattern.trim().toLowerCase();
            if (!trimmedPattern) {
                continue;
            }

            if (trimmedPattern === '*' || trimmedPattern === lowerHandlerType) {
                return true;
            }
        }

        return false;
    },

    // #endregion

    // #region Statistics Helpers (Private)

    /**
     * Recursively counts keys and arrays in a JsonObject
     * @private
     * @static
     */
    _countJsonKeysAndArrays(json: JsonObject, stats: ResourceStatistics): void {
        if (!json) return;

        for (const key of json.keys()) {
            stats.jsonKeyCount++; // Count the key itself

            const value = json.get(key);
            if (value instanceof JsonObject) {
                accessorStaticMethods._countJsonKeysAndArrays(value, stats);
            } else if (value instanceof JsonArray) {
                stats.jsonArrayCount++; // Count the array
                for (let i = 0; i < value.length; i++) {
                    const item = value.get(i);
                    if (item instanceof JsonObject) {
                        accessorStaticMethods._countJsonKeysAndArrays(item, stats);
                    }
                }
            }
        }
    },

    /**
     * Counts total handlers in a DynamicData/DynamicView config
     * Config structure: { "ComponentName": [handler1, handler2, ...], ... }
     * @private
     * @static
     */
    _countHandlersInConfig(config: JsonObject | DynamicConfigMap): number {
        let count = 0;
        const keys = config instanceof JsonObject ? config.keys() : Array.from(config.keys());

        for (const componentKey of keys) {
            const handlersArray = config instanceof JsonObject ? config.get(componentKey) : config.get(componentKey);
            if (handlersArray) {
                if (handlersArray instanceof JsonArray) {
                    count += handlersArray.length;
                } else if (Array.isArray(handlersArray)) {
                    count += handlersArray.length;
                }
            }
        }
        return count;
    }

    // #endregion
};
